import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimesCircle } from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import { skipToken } from "@reduxjs/toolkit/query";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  useListCommissionLedgersQuery,
  useGetEmployeeCommissionLedgerQuery,
} from "../../toolkitServices/employeeApi";

/* ------------ helpers ------------ */
const toNum = (v) => Number(v ?? 0) || 0;
const fmtNumberIN = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Number(n ?? 0) || 0
  );

const fmtPDFMoney = (n) => `Rs. ${fmtNumberIN(n)}`;
const fmtINR = (n) => {
  const v = Number(n ?? 0) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(v);
};

const fmtDT = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function useDebouncedValue(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const SkeletonRows = ({ cols = 9, rows = 10 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className="px-3 py-2">
            <div className="h-3 w-24 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const SkeletonCards = ({ rows = 8 }) => (
  <div className="p-3 space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="animate-pulse rounded-xl border bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="h-3 w-28 bg-slate-200 rounded" />
          <div className="h-3 w-20 bg-slate-200 rounded" />
        </div>
        <div className="mt-2 h-3 w-44 bg-slate-200 rounded" />
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="h-3 w-full bg-slate-200 rounded" />
          <div className="h-3 w-full bg-slate-200 rounded" />
        </div>
      </div>
    ))}
  </div>
);

const LedgerModal = ({ open, onClose, mode, employee }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20); // number | 0 (ALL)
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const debQ = useDebouncedValue(q, 350);

  const isAll = Number(limit) === 0;

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setLimit(20);
    setType("");
    setQ("");
  }, [open, mode, employee?._id]);

  const argsAll =
    open && mode === "all"
      ? {
          page: isAll ? 1 : page,
          limit: isAll ? 0 : Number(limit || 20),
          type: type || undefined,
          q: debQ || undefined,
        }
      : skipToken;

  const argsEmp =
    open && mode === "employee" && employee?._id
      ? {
          id: employee._id,
          page: isAll ? 1 : page,
          limit: isAll ? 0 : Number(limit || 20),
          type: type || undefined,
          q: debQ || undefined,
        }
      : skipToken;

  const allQ = useListCommissionLedgersQuery(argsAll);
  const empQ = useGetEmployeeCommissionLedgerQuery(argsEmp);

  const loading = (mode === "all" ? allQ.isFetching : empQ.isFetching) || false;
  const err = mode === "all" ? allQ.error : empQ.error;
  const payload = mode === "all" ? allQ.data : empQ.data;

  const rows = payload?.data || payload?.items || [];
  const total = payload?.total ?? rows?.length ?? 0;
  const pages =
    payload?.pages ||
    Math.max(1, Math.ceil(total / (Number(limit) || 1)));

  const title =
    mode === "employee"
      ? `Commission Ledger • ${
          `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim() ||
          "Employee"
        }`
      : "Commission Ledger • All Employees";

  const safe = (v) => (v == null ? "" : String(v));

  const getEmployeeName = (r) =>
    safe(
      r?.employeeName ||
        `${r?.employeeId?.firstName || ""} ${r?.employeeId?.lastName || ""}`.trim()
    ) || "—";

  const getContact = (r) => safe(r?.employeeId?.contact || "");
  const getEmpCode = (r) =>
    safe(r?.employeeId?.employeeCode ?? r?.employeeCode ?? "");

  /* ---------------- EXPORT DATA BUILDER ---------------- */
  const exportRows = useMemo(() => {
    const base = (r) => ({
      "Employee Code": getEmpCode(r),
      Contact: getContact(r),
      Date: fmtDT(r?.createdAt),
      Type: safe(r?.type || "—"),
      Amount: toNum(r?.amount),
      Before: toNum(r?.beforeBalance),
      After: toNum(r?.afterBalance),
      Reference: safe(r?.reference || ""),
      Notes: safe(r?.notes || ""),
      By: safe(r?.createdBy?.email || ""),
    });

    if (mode === "all") {
      return rows.map((r) => ({
        Employee: getEmployeeName(r),
        ...base(r),
      }));
    }
    return rows.map(base);
  }, [rows, mode]);

  const downloadExcel = () => {
    if (!rows?.length) return;
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");

    const fileName =
      (mode === "employee"
        ? `commission_ledger_${safe(employee?._id || "employee")}`
        : "commission_ledger_all") + ".xlsx";

    XLSX.writeFile(wb, fileName);
  };

  const downloadPDF = () => {
    if (!rows?.length) return;

    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text(title, 40, 28);
    doc.setFontSize(10);
    doc.text(`Total: ${total}`, 40, 46);

    const head =
      mode === "all"
        ? [
            [
              "Employee",
              "Emp Code",
              "Contact",
              "Date",
              "Type",
              "Amount",
              "Before",
              "After",
              "Reference",
              "Notes",
              "By",
            ],
          ]
        : [
            [
              "Emp Code",
              "Contact",
              "Date",
              "Type",
              "Amount",
              "Before",
              "After",
              "Reference",
              "Notes",
              "By",
            ],
          ];

    const body = rows.map((r) => {
      const common = [
        getEmpCode(r) || "—",
        getContact(r) || "—",
        fmtDT(r?.createdAt),
        safe(r?.type || "—"),
        fmtPDFMoney(r?.amount),
        fmtPDFMoney(r?.beforeBalance),
        fmtPDFMoney(r?.afterBalance),
        safe(r?.reference || "—"),
        safe(r?.notes || "—"),
        safe(r?.createdBy?.email || "—"),
      ];
      return mode === "all" ? [getEmployeeName(r), ...common] : common;
    });

    autoTable(doc, {
      startY: 60,
      head,
      body,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fontSize: 9 },
      margin: { left: 20, right: 20 },
    });

    const fileName =
      (mode === "employee"
        ? `commission_ledger_${safe(employee?._id || "employee")}`
        : "commission_ledger_all") + ".pdf";

    doc.save(fileName);
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-2 sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ scale: 0.98, y: 10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.98, y: 10, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className={[
              "bg-white rounded-2xl shadow-xl border overflow-hidden flex flex-col",
              "w-[96vw] h-[92vh]",
              "sm:w-full sm:h-auto sm:max-h-[90vh] sm:max-w-5xl",
            ].join(" ")}
          >
            {/* Header (responsive) */}
            <div className="px-3 sm:px-4 py-3 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-sm sm:text-base font-semibold text-slate-800 truncate">
                  {title}
                </h3>
                <p className="text-[11px] text-slate-500">
                  Total: <span className="font-semibold">{total}</span>
                </p>
              </div>

              <div className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center gap-2">
                <button
                  type="button"
                  disabled={loading || !rows?.length}
                  onClick={downloadExcel}
                  className="px-3 py-2 sm:py-1.5 rounded-xl sm:rounded-full bg-white border border-emerald-200 text-emerald-700 text-[11px] font-semibold hover:bg-emerald-50 disabled:opacity-40 w-full sm:w-auto"
                >
                  Download Excel
                </button>

                <button
                  type="button"
                  disabled={loading || !rows?.length}
                  onClick={downloadPDF}
                  className="px-3 py-2 sm:py-1.5 rounded-xl sm:rounded-full bg-white border border-rose-200 text-rose-700 text-[11px] font-semibold hover:bg-rose-50 disabled:opacity-40 w-full sm:w-auto"
                >
                  Download PDF
                </button>

                <button
                  className="text-slate-500 hover:text-slate-700 self-end sm:self-auto"
                  onClick={onClose}
                  title="Close"
                >
                  <FaTimesCircle />
                </button>
              </div>
            </div>

            {/* Filters (responsive) */}
            <div className="px-3 sm:px-4 py-2 border-b bg-slate-50 flex flex-col sm:flex-row flex-wrap gap-2 items-stretch sm:items-end">
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-[10px] text-slate-600">Search</label>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="reference / notes…"
                  className="w-full sm:w-[240px] px-3 py-2 sm:py-1.5 rounded-lg text-[11px] border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                />
              </div>

              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-[10px] text-slate-600">Type</label>
                <select
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setPage(1);
                  }}
                  className="w-full sm:w-[170px] px-2.5 py-2 sm:py-1.5 rounded-lg text-[11px] border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                >
                  <option value="">All</option>
                  <option value="WITHDRAW">WITHDRAW</option>
                  <option value="CREDIT">CREDIT</option>
                  <option value="ADJUST">ADJUST</option>
                </select>
              </div>

              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-[10px] text-slate-600">Rows</label>
                <select
                  value={isAll ? "ALL" : String(limit)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLimit(v === "ALL" ? 0 : Number(v) || 20);
                    setPage(1);
                  }}
                  className="w-full sm:w-[140px] px-2.5 py-2 sm:py-1.5 rounded-lg text-[11px] border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                >
                  <option value="ALL">All</option>
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => {
                  setQ("");
                  setType("");
                  setPage(1);
                }}
                className="sm:ml-auto inline-flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 rounded-xl sm:rounded-full bg-white border border-slate-200 text-[11px] text-slate-700 hover:bg-slate-100 w-full sm:w-auto"
              >
                <FiRotateCcw /> Reset
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {err ? (
                <div className="py-10 text-center text-rose-600 text-sm">
                  Failed to load ledger
                </div>
              ) : (
                <>
                  {/* ✅ MOBILE VIEW (cards) */}
                  <div className="sm:hidden h-full overflow-auto">
                    {loading ? (
                      <SkeletonCards rows={8} />
                    ) : rows?.length ? (
                      <div className="p-3 space-y-2">
                        {rows.map((r, idx) => (
                          <div
                            key={r?._id || idx}
                            className="rounded-xl border bg-white p-3 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                {mode === "all" ? (
                                  <div className="text-[12px] font-semibold text-slate-800 truncate">
                                    {getEmployeeName(r)}
                                  </div>
                                ) : null}
                                <div className="text-[11px] text-slate-600">
                                  Emp Code:{" "}
                                  <span className="font-semibold text-slate-800">
                                    {getEmpCode(r) || "—"}
                                  </span>
                                </div>
                              </div>

                              <span className="px-2 py-0.5 rounded-full border text-[10px] text-slate-700 shrink-0">
                                {r?.type || "—"}
                              </span>
                            </div>

                            <div className="mt-2 text-[11px] text-slate-600">
                              {fmtDT(r?.createdAt)}
                            </div>

                            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                              <div className="rounded-lg bg-slate-50 border p-2">
                                <div className="text-[10px] text-slate-500">Amount</div>
                                <div className="font-semibold text-slate-800">
                                  {fmtINR(r?.amount)}
                                </div>
                              </div>
                              <div className="rounded-lg bg-slate-50 border p-2">
                                <div className="text-[10px] text-slate-500">After</div>
                                <div className="font-semibold text-slate-800">
                                  {fmtINR(r?.afterBalance)}
                                </div>
                              </div>
                            </div>

                            <div className="mt-2 text-[11px] text-slate-700">
                              <div className="truncate">
                                <span className="text-slate-500">Contact:</span>{" "}
                                {getContact(r) || "—"}
                              </div>
                              <div className="truncate">
                                <span className="text-slate-500">Ref:</span>{" "}
                                {r?.reference || "—"}
                              </div>
                              <div className="truncate">
                                <span className="text-slate-500">By:</span>{" "}
                                {r?.createdBy?.email || "—"}
                              </div>
                            </div>

                            {r?.notes ? (
                              <div className="mt-2 text-[11px] text-slate-600">
                                <span className="text-slate-500">Notes:</span>{" "}
                                {r?.notes}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-10 text-center text-slate-500 text-[12px]">
                        No ledger entries found.
                      </div>
                    )}
                  </div>

                  {/* ✅ TABLET/DESKTOP VIEW (table + horizontal scroll) */}
                  <div className="hidden sm:block h-full overflow-auto">
                    <div className="overflow-x-auto">
                      <table className="min-w-[1050px] w-full text-left">
                        <thead className="sticky top-0 z-10 bg-white border-b">
                          <tr className="text-[10px] uppercase text-slate-600">
                            {mode === "all" && (
                              <th className="px-3 py-2 whitespace-nowrap">
                                Employee
                              </th>
                            )}
                            <th className="px-3 py-2 whitespace-nowrap">Emp Code</th>
                            <th className="px-3 py-2 whitespace-nowrap">Contact</th>
                            <th className="px-3 py-2 whitespace-nowrap">Date</th>
                            <th className="px-3 py-2 whitespace-nowrap">Type</th>
                            <th className="px-3 py-2 whitespace-nowrap">Amount</th>
                            <th className="px-3 py-2 whitespace-nowrap">Before</th>
                            <th className="px-3 py-2 whitespace-nowrap">After</th>
                            <th className="px-3 py-2 whitespace-nowrap">Reference</th>
                            <th className="px-3 py-2 whitespace-nowrap">Notes</th>
                            <th className="px-3 py-2 whitespace-nowrap">By</th>
                          </tr>
                        </thead>

                        <tbody className="text-[11px] text-slate-700">
                          {loading ? (
                            <SkeletonRows cols={mode === "all" ? 11 : 10} rows={10} />
                          ) : rows?.length ? (
                            rows.map((r, idx) => (
                              <tr
                                key={r?._id || idx}
                                className="border-b hover:bg-slate-50"
                              >
                                {mode === "all" && (
                                  <td className="px-3 py-2">
                                    {getEmployeeName(r)}
                                  </td>
                                )}
                                <td className="px-3 py-2 font-semibold">
                                  {getEmpCode(r) || "—"}
                                </td>
                                <td className="px-3 py-2">{getContact(r) || "—"}</td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {fmtDT(r?.createdAt)}
                                </td>
                                <td className="px-3 py-2">
                                  <span className="px-2 py-0.5 rounded-full border text-[10px]">
                                    {r?.type || "—"}
                                  </span>
                                </td>
                                <td className="px-3 py-2 font-semibold">
                                  {fmtINR(r?.amount)}
                                </td>
                                <td className="px-3 py-2">{fmtINR(r?.beforeBalance)}</td>
                                <td className="px-3 py-2">{fmtINR(r?.afterBalance)}</td>
                                <td className="px-3 py-2 max-w-[240px] truncate">
                                  {r?.reference || "—"}
                                </td>
                                <td className="px-3 py-2 max-w-[240px] truncate">
                                  {r?.notes || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {r?.createdBy?.email || "—"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={mode === "all" ? 11 : 10}
                                className="px-4 py-10 text-center text-slate-500"
                              >
                                No ledger entries found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Pagination (responsive) - hide when ALL */}
            {!isAll && (
              <div className="px-3 sm:px-4 py-3 border-t bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px]">
                <span className="text-slate-600">
                  Page <span className="font-semibold">{page}</span> /{" "}
                  <span className="font-semibold">{pages}</span>
                </span>

                <div className="flex items-center justify-between sm:justify-end gap-2">
                  <button
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-2 sm:px-2 sm:py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 w-full sm:w-auto"
                  >
                    Prev
                  </button>
                  <button
                    disabled={page >= pages || loading}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    className="px-3 py-2 sm:px-2 sm:py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 w-full sm:w-auto"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default LedgerModal;
